import type { GetUploadUrlResponse } from '../modules/uploader/Uploader.interface';

import type {
  AnalyzeImageFindingsDto,
  AnalyzeImageVideaError,
  AnalyzeImageVideaErrorCode,
  ImageAnalysisState,
  ImageKeys,
  LocationDetail,
  Meta,
  PmsType,
  Provider,
  TImagetype,
  UserModifications,
} from './global';
import type { InferenceServerAnalyzeResponse } from './inference-server-types';

/**
 * Contract between Nucleus and detect
 */
export type AnalyzeImageRequest = {
  organizationId: string;
  practiceId: string;
  patientId: string;
  fileName: string;
  forceAnalyze: boolean;
};

export type AnalyzeImageResponse = {
  imageId: string;
  analysisState: ImageAnalysisState;
  detectedImageType: TImagetype | null;
  videaErrorCode: AnalyzeImageVideaErrorCode | null;
  models: AnalyzeImageFindingsDto | null;
  imageKeys: ImageKeys | null;
  videaError?: AnalyzeImageVideaError | null;
  imageRerunStatus?: ImageRerunStatus;
  userModifications?: UserModifications;
};

export type PresignedUrlRequest = {
  fileName: string;
  patientId: string;
};
export type PresignedUrlResponse = {
  data: GetUploadUrlResponse;
};

export type FindingStatsForImage = {
  numberOfCaries: number;
  rblSeverities: {
    normal: number;
    mild: number;
    moderate: number;
    severe: number;
  };
  detectedImageTypes: string[];
};

export type InsightsTreatmentFacet = {
  id?: string;
  color?: string;
  treatmentType: string;
  quantityCompleted: number;
  totalRevenue: number;
  caseCompletionRate: number;
};

export type InsightsPatientWithTreatment = {
  findingType: string;
  amount: number;
};

export type InsightsRevenue = {
  totalRestorative: number;
};

export type InsightsTreatments = {
  totalPresented: number;
  totalCompleted: number;
  caseCompletionRateQuantity: number;
  caseCompletionRateRevenue: number;
  facets?: InsightsTreatmentFacet[];
};

export type InsightsPracticeTreatments = {
  totalPresented: number;
  totalCompleted: number;
  caseCompletionQuantity: number;
  caseCompletionRevenue: number;
  patientDiagnosticPercentage: number;
};

export type InsightsPatients = {
  totalUnique: number;
  uniqueWithAiFindings: InsightsPatientWithTreatment[];
};

export type InsightsAiMetrics = {
  aiDiagnosticRate: number;
};

export type InsightsPracticeAiMetrics = {
  aiDiagnosticRate: number;
  aiUtilizationRate: number;
  uniquePatientsRestoAiFindings: number;
  totalFindings: number;
};

export type InsightsSummary = {
  revenue: InsightsRevenue;
  treatments?: InsightsTreatments;
  patients: InsightsPatients;
  aiMetrics?: InsightsAiMetrics;
};

export type InsightsPracticeResponse = {
  data: InsightsPractice[];
  meta?: Meta;
};

export type InsightsPractice = {
  id: string;
  aiMetrics?: InsightsPracticeAiMetrics;
  practiceName: string;
  revenue: InsightsRevenue;
  treatments?: InsightsPracticeTreatments;
};

export type InsightsProductionMetrics = {
  revenue: number;
  treatmentsCompleted: number;
  treatmentsProposed: number;
};

export type InsightsProductionPeriod = {
  start: string;
  end: string;
  metrics: InsightsProductionMetrics;
};

export type GetOverallInsightsResponse = {
  totalRestorativeRevenue: any;
  revenue: InsightsRevenue;
  treatments: InsightsTreatments;
  patients: InsightsPatients;
  aiMetrics: InsightsAiMetrics;
  currencyCode?: string;
  lastRefreshedDate?: string;
  pmsType?: PmsType;
};

export type ProductionValue = {
  cariesProductionValue: number;
  rblProductionValue: number;
};

export type FindingStatsForImageWithDetectedImageType = FindingStatsForImage & {
  detectedImageTypes: string[];
};

export type FindingStatsForPatients = {
  patients: {
    today: {
      [key: string]: FindingStatsForImageWithDetectedImageType;
    };
    previous: {
      [key: string]: FindingStatsForImageWithDetectedImageType;
    };
  };
  totalFindingsToday: FindingStatsForImage;
  productionValueToday: ProductionValue;
};

export type GetLocationsByRevenueRequest = {
  startDate: string;
  endDate: string;
  order: string;
  sortBy: string;
  pageSize: number;
  page?: number;
  filter?: string;
};

export type FetchVideaImageIdResponse = {
  videaImageId: string;
  estimatedVisitId: number | null;
  unifiedAppointmentId: number | null;
};

export type FetchVideaPatientIdResponse = {
  patientId: string;
  /**
   * Videa Id for the practice that is associated patient
   * this prioritizes the practice associated with the pmsPatient (matched through patientIdMatches)
   * then falls back to the practiceId associated with the imaging patient.
   * this was to facilitate dentrix enterprise multi practice users.
   *
   * this prioritizes the practice videa id associated with the unified appointment then the
   * pms patient and finally the imaging patient
   */
  practiceVideaId?: string | null;
  /** debug only */
  unifiedAppointmentPracticeVideaId?: string | null;
  /** debug only */
  imagingPatientsPracticeId?: string;
  /** debug only */
  pmsPatientsPracticeId?: string | null;
};

export type GetLocationsDetailResponse = {
  practice: LocationDetail;
};

export enum RotationRequestVideaErrorCode {
  S3_PATH_NOT_FOUND = 'S3_PATH_NOT_FOUND',
  UNKNOWN = 'UNKNOWN',
}

export enum ImageRerunStatus {
  SUCCESS = 'SUCCESS',
  PREPROCESS_FAILED = 'PREPROCESS_FAILED',
  ANALYSIS_FAILED = 'ANALYSIS_FAILED',
  SHARPENING_FAILED = 'SHARPENING_FAILED',
  S3_PATH_NOT_FOUND = 'S3_PATH_NOT_FOUND',
  UNKNOWN = 'UNKNOWN',
}

export type SharpenResult = {
  sharpenedFullS3Path: string | null;
  generalizedAdjustmentFullS3Path: string | null;
};

export type GetAnalysisResultsReturn = {
  imageId: string;
  errorCode?: RotationRequestVideaErrorCode | AnalyzeImageVideaErrorCode;
  rerunStatus: ImageRerunStatus;
  S3Key?: string;
  analysisResult?: InferenceServerAnalyzeResponse;
  sharpenResult?: SharpenResult;
  S3Path?: string;
  fileName?: string;
  folderHash?: string;
  rotationApplied?: number;
};

export type GetProviderSummaryRequest = {
  startDate: string;
  endDate: string;
  order: string;
  sortBy: string;
  pageSize: number;
};

export type GetProviderSummaryResponse = {
  data: Array<Provider>;
  meta: Meta;
  message: string;
};

export type GetInsuranceClaimSummaryRequest = {
  patientId: string;
  patientName: string;
  imageAnalysisRecordIds: string[];
};

export type GetInsuranceClaimSummaryResponse = {
  insuranceClaimSummaryPdfPath: string;
};
